import type { RouterConfig } from '@nuxt/schema';
import type { RouteRecordRaw, RouteLocationNormalized } from 'vue-router';
import { adminRoutes } from '~/admin/routes';
import {
  EVENT_VIEW_MODES,
  EVENT_MEDIA_MODES,
  EVENTS_ROUTE_NAMES,
  EVENTS_GALLERY_DETAIL_ROUTE_NAMES,
} from '~/shared/lib/events-routes.const';
import {
  JOURNAL_ROUTE_NAMES,
  isJournalListingRoute,
} from '~/shared/lib/journal-routes.const';

export default {
  // https://router.vuejs.org/api/interfaces/routeroptions.html#routes
  routes: (_routes) => {
    let isBrokerPass = false;

    if (import.meta.server) {
      const { hostname } = useRequestURL();
      isBrokerPass = hostname.includes('brokerpass');
    } else if (import.meta.client) {
      isBrokerPass = window.location.hostname.includes('brokerpass');
    }

    const customRoutes: RouteRecordRaw[] = [
      {
        name: 'manage',
        path: `/manage`,
        meta: { layout: false as const },
        component: () => import('~/admin/app/App.vue'),
        children: adminRoutes,
      },
      {
        name: 'home',
        path: '/',
        meta: isBrokerPass ? { layout: false as const } : {},
        component: isBrokerPass
          ? () => import('~/pages/broker-pass/ui/BrokerPage.vue')
          : () => import('~/pages/home-page/ui/HomePage.vue'),
      },
      {
        name: 'tag-ai',
        path: '/tag/ai/:id(\\d+)/',
        component: () => import('~/pages/tag-ai/ui/TagAiPage.vue'),
      },
      {
        name: 'tag',
        path: '/tag/:id(\\d+)/',
        component: () => import('~/pages/tag/ui/TagPage.vue'),
      },
      {
        name: 'news',
        path: '/:newsType(news|analytics|expert|new_object)/:slug(category|author)/:id?',
        component: () => import('~/pages/news/ui/News.vue'),
        props: (route: RouteLocationNormalized) => ({
          newsType: route.params.newsType,
          categoryId:
            route.params.slug === 'category' && route.params.id
              ? Number(route.params.id)
              : null,
          authorId:
            route.params.slug === 'author' && route.params.id
              ? Number(route.params.id)
              : null,
        }),
      },
      {
        name: 'news-short',
        path: '/:slug(category|author)/:id?',
        component: () => import('~/pages/news/ui/News.vue'),
        props: (route: RouteLocationNormalized) => ({
          newsType: undefined,
          categoryId:
            route.params.slug === 'category' && route.params.id
              ? Number(route.params.id)
              : null,
          authorId:
            route.params.slug === 'author' && route.params.id
              ? Number(route.params.id)
              : null,
        }),
      },
      {
        name: 'news-type',
        path: '/:newsType(news|analytics|expert|new_object)',
        component: () => import('~/pages/news/ui/News.vue'),
        props: (route: RouteLocationNormalized) => ({
          newsType: route.params.newsType,
          categoryId: null,
          authorId: null,
        }),
      },
      {
        name: 'partners-news',
        path: '/partners',
        component: () => import('~/pages/news').then((m) => m.News),
        props: { partner: true, withExperts: true },
      },
      {
        name: 'news-id',
        path: '/:newsType(news|analytics|expert)/:id',
        meta: { layoutType: 'article' },
        component: () => import('~/pages/article/ui/ArticlePage.vue'),
      },
      {
        name: 'news-new-objects-id',
        path: '/new_object/:id',
        meta: { layout: 'landing' },
        component: () => import('~/pages/article/ui/ArticleNewObjectPage.vue'),
      },
      {
        name: 'news-preview',
        path: '/preview',
        meta: { layoutType: 'article' },
        component: () => import('~/pages/article/ui/ArticlePage.vue'),
      },
      {
        path: '/events',
        meta: { layout: 'events' },
        component: () => import('~/pages/events/ui/EventsRouterView.vue'),
        children: [
          {
            name: EVENTS_ROUTE_NAMES.afisha,
            path: '',
            meta: { tab: EVENT_VIEW_MODES.afisha },
            component: () => import('~/pages/events').then((m) => m.EventsPage),
          },
          {
            name: EVENTS_ROUTE_NAMES.photos,
            path: 'photos',
            meta: {
              tab: EVENT_VIEW_MODES.media,
              mediaMode: EVENT_MEDIA_MODES.photos,
            },
            component: () => import('~/pages/events').then((m) => m.EventsPage),
          },
          {
            name: EVENTS_ROUTE_NAMES.videos,
            path: 'videos',
            meta: {
              tab: EVENT_VIEW_MODES.media,
              mediaMode: EVENT_MEDIA_MODES.videos,
            },
            component: () => import('~/pages/events').then((m) => m.EventsPage),
          },
          {
            name: EVENTS_ROUTE_NAMES.schedule,
            path: 'schedule',
            meta: { tab: EVENT_VIEW_MODES.schedule },
            component: () => import('~/pages/events').then((m) => m.EventsPage),
          },
          {
            name: EVENTS_GALLERY_DETAIL_ROUTE_NAMES.photos,
            path: 'photo-gallery/:id',
            meta: {
              tab: EVENT_VIEW_MODES.media,
              mediaMode: EVENT_MEDIA_MODES.photos,
            },
            component: () =>
              import('~/pages/gallery-detail').then(
                (m) => m.EventsPhotoGalleryDetailPage,
              ),
          },
          {
            name: EVENTS_GALLERY_DETAIL_ROUTE_NAMES.videos,
            path: 'video-gallery/:id',
            meta: {
              tab: EVENT_VIEW_MODES.media,
              mediaMode: EVENT_MEDIA_MODES.videos,
            },
            component: () =>
              import('~/pages/gallery-detail').then(
                (m) => m.EventsVideoGalleryDetailPage,
              ),
          },
          {
            name: 'event-detail',
            path: ':id',
            component: () =>
              import('~/pages/event-detail').then((m) => m.EventDetailPage),
          },
          {
            name: 'speaker-detail',
            path: 'speakers/:id',
            component: () =>
              import('~/pages/speaker-detail').then((m) => m.SpeakerDetailPage),
          },
        ],
      },
      {
        path: '/journal',
        meta: { layoutType: 'article' },
        component: () => import('~/pages/journal/ui/JournalRouterView.vue'),
        children: [
          {
            name: JOURNAL_ROUTE_NAMES.index,
            path: '',
            component: () =>
              import('~/pages/journal').then((m) => m.JournalPage),
          },
          {
            // Keeps every current cre.ru/journal URL alive, nested editions
            // included; the component picks the screen the address names
            name: JOURNAL_ROUTE_NAMES.section,
            path: ':section+',
            component: () =>
              import('~/pages/journal').then((m) => m.JournalSectionRoute),
          },
        ],
      },
      {
        name: 'search',
        path: '/search',
        component: () => import('~/pages/search/ui/Search.vue'),
      },
      {
        name: 'advertising',
        path: '/advertising',
        component: () => import('~/pages/advertising/ui/Advertising.vue'),
      },
      {
        name: 'advertising_main',
        path: '/advertising_main',
        component: () =>
          import('~/pages/advertising_main/ui/AdvertisingMain.vue'),
      },
      {
        name: 'contacts_main',
        path: '/contacts_main',
        component: () => import('~/pages/contacts/ui/ContactsPage.vue'),
      },
      {
        name: 'page404',
        path: '/error404',
        meta: { layout: false as const },
        component: () => import('~/error.vue'),
      },
      {
        name: 'catch-all',
        path: '/:pathMatch(.*)*', // Captures all unmatched routes
        meta: { layout: false as const },
        component: () => import('~/error.vue'), // Points to your custom 404 page
      },
    ];

    return customRoutes;
  },

  // Replaces Nuxt's default (nuxt/dist/pages/runtime/router.options.js in
  // 3.16), which scrolls to the top on every path change and ignores
  // `meta.scrollToTop: false` when the path differs
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;

    if (to.path === from.path) {
      if (to.hash) return { el: to.hash, top: getScrollMarginTop(to.hash) };
      return from.hash ? { left: 0, top: 0 } : false;
    }

    // Tabs and chips of the journals switch the listing in place
    if (isJournalListingRoute(to) && isJournalListingRoute(from)) return false;

    // Wait for the new page to render, or the scroll lands on the old one
    const nuxtApp = useNuxtApp();

    return new Promise((resolve) => {
      nuxtApp.hooks.hookOnce('page:loading:end', () => {
        requestAnimationFrame(() =>
          resolve(
            to.hash
              ? { el: to.hash, top: getScrollMarginTop(to.hash) }
              : { left: 0, top: 0 },
          ),
        );
      });
    });
  },
} satisfies RouterConfig;

function getScrollMarginTop(selector: string): number {
  try {
    const element = document.querySelector(selector);

    if (!element) return 0;

    return (
      (Number.parseFloat(getComputedStyle(element).scrollMarginTop) || 0) +
      (Number.parseFloat(
        getComputedStyle(document.documentElement).scrollPaddingTop,
      ) || 0)
    );
  } catch {
    return 0;
  }
}
